import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Button,
  Checkbox,
  CustomerDropDown,
  DropDown,
  Icon,
  Loader,
  Modal,
  TabSelector,
} from "@src/components/atoms";
import { useAuthContext } from "@src/context";
import AdminHooks from "@src/context/hooks/adminHooks";
import { CardContainer } from "@src/components/cards";

const DisplayTemplateConfig = ({ templateConfig, jsonMode = false }) => {
  if (!templateConfig) return null;

  if (jsonMode) {
    return (
      <pre className="bg-neutral-100 p-4 text-xs rounded">
        {JSON.stringify(templateConfig, null, 2)}
      </pre>
    );
  }

  //Format JSON as a Table
  const isOverride = templateConfig.customerCode !== "DEFAULT";
  const keysToSkip = ["templateCode", ...(isOverride ? [] : ["customerCode"])];
  return (
    <div
      className={`mb-4 p-4 bg-neutral-50 rounded shadow-sm border border-gray-200 overflow-x-auto`}
      title={JSON.stringify(templateConfig, null, 2)}
    >
      <table className="w-full table-auto text-sm">
        <tbody>
          {/* Dump all the keys/values dynamically rather than a specific list of properties */}
          {Object.entries(templateConfig)
            .filter(([key, _]) => !keysToSkip.includes(key))
            .map(([key, value]) => (
              <tr key={key}>
                <th className="text-left font-semibold pr-4 py-1 w-48">
                  {key}
                </th>
                <td className="py-1">
                  {typeof value === "string" ||
                  typeof value === "number" ||
                  typeof value === "boolean" ? (
                    String(value)
                  ) : (
                    <code className="bg-neutral-100 p-1 text-xs">
                      {JSON.stringify(value)}
                    </code>
                  )}
                </td>
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );
};

/** Given a list of template configs, outputs one CardContainer per templateCode */
const ListAllTemplateConfigs = ({ templateConfigs, jsonMode = false }) => {
  // Group templates by templateCode
  const grouped = templateConfigs.reduce((acc, config) => {
    const key = config.templateCode;
    if (!acc[key]) acc[key] = [];
    acc[key].push(config);
    return acc;
  }, {});

  return (
    <div className="space-y-4">
      {Object.entries(grouped).map(([templateCode, configs]) => {
        //There should always be a customerCode:DEFAULT for each template
        const defaultEntry = configs.find((c) => c.customerCode === "DEFAULT");
        const overrides = configs.filter((c) => c.customerCode !== "DEFAULT");

        return (
          <CardContainer key={templateCode} padding={3} flexibleHeight={true}>
            <div className="mb-2">
              <h2>{templateCode}</h2>
            </div>

            {/* DEFAULT */}
            <DisplayTemplateConfig
              templateConfig={defaultEntry}
              jsonMode={jsonMode}
            />

            {overrides.length > 0 && (
              <>
                <hr className="my-2 border-gray-300" />
                <h3>Overrides</h3>
                {overrides.map((entry) => (
                  <DisplayTemplateConfig
                    key={entry.customerCode}
                    templateConfig={entry}
                    jsonMode={jsonMode}
                  />
                ))}
              </>
            )}
          </CardContainer>
        );
      })}
    </div>
  );
};

/** Displays all data from (Notifications DB).templates */
const NotificationTemplatesPage = () => {
  const auth = useAuthContext();
  const [jsonMode, setJsonMode] = useState(false);
  const {
    data: templateConfigs,
    isPending,
    isError,
  } = AdminHooks.useGetNotificationTemplateConfigs(auth.activeRole);

  return (
    <div className="p-2 flex flex-col gap-4">
      <h2 className="text-center p-2 relative">
        Notification Templates
        <a href="https://app.clickup.com/2193384/v/dc/22xz8-6948/22xz8-48072">
          Documentation (ClickUp Wiki)
        </a>
        <span className="float-right">
          <Checkbox
            label="JSON mode"
            checked={jsonMode}
            onChange={(e) => setJsonMode(e.target.checked)}
          />
        </span>
      </h2>

      {isPending ? (
        <Loader size="xl" />
      ) : isError ? (
        "Error loading notification template configs"
      ) : (
        <ListAllTemplateConfigs
          templateConfigs={templateConfigs}
          jsonMode={jsonMode}
        />
      )}
    </div>
  );
};

export default NotificationTemplatesPage;
